// rooms.ts - typed client for /api/v1/rooms endpoints.

export interface CreateRoomResponse {
  roomId: string;
  createdAt: string;
  expiresAt: string;
  managementToken: string;
  accessToken: string;
  limits: { maxBytes: number; maxItems: number };
}

export interface RoomMetadata {
  roomId: string;
  createdAt: string;
  expiresAt: string;
  limits: { maxBytes: number; maxItems: number };
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly requestId: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(resp: Response): Promise<ApiError> {
  try {
    const body = await resp.json();
    const err = body.error;
    if (err && err.code) {
      return new ApiError(err.code, resp.status, err.requestId ?? '', err.message ?? 'unknown error');
    }
  } catch {
    // Non-JSON response.
  }
  return new ApiError('UNKNOWN', resp.status, '', `HTTP ${resp.status}`);
}

export async function createRoom(ttlSeconds?: number): Promise<CreateRoomResponse> {
  const body = ttlSeconds != null ? JSON.stringify({ ttlSeconds }) : '{}';
  const resp = await fetch('/api/v1/rooms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  if (!resp.ok) throw await parseError(resp);
  return resp.json();
}

export async function getRoom(roomId: string): Promise<RoomMetadata> {
  const resp = await fetch(`/api/v1/rooms/${roomId}`);
  if (!resp.ok) throw await parseError(resp);
  return resp.json();
}

export async function deleteRoom(roomId: string, managementToken: string): Promise<void> {
  const resp = await fetch(`/api/v1/rooms/${roomId}`, {
    method: 'DELETE',
    headers: { 'X-Room-Token': managementToken },
  });
  if (!resp.ok && resp.status !== 404) throw await parseError(resp);
}
